import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';

interface EmailComposerProps {
  candidateName: string;
  candidateEmail: string;
  jobTitle: string;
  applicationId?: string;
  interviewDetails?: {
    date: string;
    time: string;
    type: string;
    link?: string;
    location?: string;
  };
  onClose: () => void;
  onSuccess: () => void;
}

const EMAIL_TEMPLATES = {
  interview_invitation: {
    name: 'Interview Invitation',
    subject: 'Interview Invitation - {{jobTitle}} at {{companyName}}',
    body: `Dear {{candidateName}},

We're pleased to invite you to interview for the {{jobTitle}} position at {{companyName}}.

Interview Details:
- Date: {{interviewDate}}
- Time: {{interviewTime}}
- Type: {{interviewType}}
{{#if videoLink}}
- Meeting Link: {{videoLink}}
{{/if}}
{{#if location}}
- Location: {{location}}
{{/if}}
- Duration: {{duration}} minutes

Please confirm your attendance by replying to this email.

We look forward to speaking with you!

Best regards,
{{senderName}}
{{companyName}}`,
    icon: '📅'
  },
  rejection: {
    name: 'Application Rejection',
    subject: 'Update on Your Application - {{jobTitle}}',
    body: `Dear {{candidateName}},

Thank you for your interest in the {{jobTitle}} position at {{companyName}} and for taking the time to apply.

After careful consideration, we have decided to move forward with other candidates whose qualifications more closely match our current needs.

We appreciate your interest in {{companyName}} and encourage you to apply for future positions that match your skills and experience.

We wish you the best in your job search.

Best regards,
{{senderName}}
{{companyName}}`,
    icon: '❌'
  },
  offer_letter: {
    name: 'Job Offer',
    subject: 'Job Offer - {{jobTitle}} at {{companyName}}',
    body: `Dear {{candidateName}},

Congratulations! We are pleased to offer you the position of {{jobTitle}} at {{companyName}}.

Position Details:
- Job Title: {{jobTitle}}
- Department: {{department}}
- Start Date: {{startDate}}
- Salary: {{salary}}
- Benefits: {{benefits}}

Please review the attached formal offer letter and return a signed copy by {{offerDeadline}}.

If you have any questions, please don't hesitate to reach out.

We're excited to have you join our team!

Best regards,
{{senderName}}
{{companyName}}`,
    icon: '✉️'
  },
  follow_up: {
    name: 'Follow Up',
    subject: 'Following Up - {{jobTitle}} Application',
    body: `Dear {{candidateName}},

I wanted to follow up on your application for the {{jobTitle}} position at {{companyName}}.

We've reviewed your application and would like to learn more about your experience and qualifications.

Are you available for a brief conversation this week?

Please let me know your availability.

Best regards,
{{senderName}}
{{companyName}}`,
    icon: '👋'
  },
  thank_you: {
    name: 'Thank You',
    subject: 'Thank You for Interviewing - {{jobTitle}}',
    body: `Dear {{candidateName}},

Thank you for taking the time to interview for the {{jobTitle}} position at {{companyName}}.

It was a pleasure speaking with you and learning more about your experience and qualifications.

We will review all candidates and will be in touch soon regarding next steps.

Thank you again for your interest in {{companyName}}.

Best regards,
{{senderName}}
{{companyName}}`,
    icon: '🙏'
  },
  custom: {
    name: 'Custom Email',
    subject: '',
    body: '',
    icon: '✏️'
  }
};

const EmailComposer: React.FC<EmailComposerProps> = ({
  candidateName,
  candidateEmail,
  jobTitle,
  applicationId,
  interviewDetails,
  onClose,
  onSuccess,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<keyof typeof EMAIL_TEMPLATES>('custom');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  // Template variables
  const variables = {
    candidateName,
    jobTitle,
    companyName: 'Explanatum', // Could be from settings
    senderName: 'Hiring Team', // Could be from user profile
    interviewDate: interviewDetails?.date ? new Date(interviewDetails.date).toLocaleDateString() : '',
    interviewTime: interviewDetails?.time || '',
    interviewType: interviewDetails?.type || '',
    videoLink: interviewDetails?.link || '',
    location: interviewDetails?.location || '',
    duration: '60',
    department: 'Engineering', // Could come from job data
    startDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
    salary: '£60,000 - £80,000',
    benefits: 'Health insurance, Pension, 25 days holiday',
    offerDeadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
  };

  const replaceVariables = (text: string): string => {
    let result = text;
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      result = result.replace(regex, value || '');
    });
    // Handle conditionals (simple implementation)
    result = result.replace(/{{#if \w+}}[\s\S]*?{{\/if}}/g, (match) => {
      const varName = match.match(/{{#if (\w+)}}/)?.[1];
      if (varName && variables[varName as keyof typeof variables]) {
        return match.replace(/{{#if \w+}}/, '').replace(/{{\/if}}/, '');
      }
      return '';
    });
    return result;
  };

  useEffect(() => {
    if (selectedTemplate !== 'custom') {
      const template = EMAIL_TEMPLATES[selectedTemplate];
      setSubject(replaceVariables(template.subject));
      setBody(replaceVariables(template.body));
    }
  }, [selectedTemplate]);

  const handleSend = async () => {
    try {
      setSending(true);

      if (!subject.trim()) {
        toast.error('Subject is required');
        return;
      }

      if (!body.trim()) {
        toast.error('Email body is required');
        return;
      }

      // TODO: Implement actual email sending via API
      // For now, simulate sending
      await new Promise(resolve => setTimeout(resolve, 1500));

      toast.success(`Email sent to ${candidateEmail}!`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to send email:', error);
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const insertVariable = (varName: string) => {
    const textarea = document.getElementById('email-body') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = body;
      const before = text.substring(0, start);
      const after = text.substring(end);
      setBody(before + `{{${varName}}}` + after);
      
      // Set cursor position after inserted variable
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + varName.length + 4;
        textarea.focus();
      }, 0);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Send Email</h2>
            <p className="text-sm text-gray-500 mt-1">
              To: {candidateName} ({candidateEmail})
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Template Selection */}
          <section>
            <label className="block text-sm font-medium text-gray-700 mb-3">Email Template</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(EMAIL_TEMPLATES).map(([key, template]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedTemplate(key as keyof typeof EMAIL_TEMPLATES)}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    selectedTemplate === key
                      ? 'border-purple-600 bg-purple-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">{template.icon}</span>
                    <span className="font-medium text-sm text-gray-900">{template.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Subject */}
          <section>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </section>

          {/* Variable Inserter */}
          <section>
            <label className="block text-sm font-medium text-gray-700 mb-2">Insert Variable</label>
            <div className="flex flex-wrap gap-2">
              {Object.keys(variables).map((varName) => (
                <button
                  key={varName}
                  type="button"
                  onClick={() => insertVariable(varName)}
                  className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                >
                  {varName}
                </button>
              ))}
            </div>
          </section>

          {/* Body */}
          <section>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Body <span className="text-red-500">*</span>
            </label>
            <textarea
              id="email-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Compose your email..."
              rows={12}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-2">
              Use {`{{variableName}}`} syntax or click variable buttons above to insert dynamic content
            </p>
          </section>

          {/* Preview */}
          <section className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Preview</h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium text-gray-700">Subject:</span>
                <p className="text-gray-900 mt-1">{subject || '(No subject)'}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Body:</span>
                <pre className="text-gray-900 mt-1 whitespace-pre-wrap font-sans">
                  {replaceVariables(body) || '(Empty)'}
                </pre>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            disabled={sending}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={sending}
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <span>✉️</span>
                <span>Send Email</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailComposer;
